import { DayView } from "../models/itinerary-day.model";

/**
 * Come si chiama un giorno nella UI.
 *
 * Un giorno di un template non ha data e si chiama con la sua posizione ("Giorno 3");
 * uno di un itinerario collegato si chiama con la sua data. La regola stava scritta in
 * quattro punti (sidebar, builder, checkout, selettori) con locale e formati divergenti:
 * qui è una sola, e i formatter `Intl` si costruiscono una volta per locale invece che
 * a ogni giorno di ogni render.
 */

type MonthStyle = 'short' | 'long';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const formatter = (locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const cacheKey = `${locale}|${JSON.stringify(options)}`;
  let cached = formatterCache.get(cacheKey);
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(cacheKey, cached);
  }
  return cached;
};

export interface DayLabelOptions {
  locale?: string;
  /** `short` per i menu compatti (12 mag), `long` per l'intestazione del giorno. */
  month?: MonthStyle;
  /** Etichetta da usare quando il giorno non ha data (template). */
  fallback: (index: number) => string;
}

/** Titolo del giorno: "Giorno 3" su un template, "12 maggio" su un itinerario datato. */
export const formatDayTitle = (
  day: Pick<DayView, 'date' | 'index'>,
  { locale = 'it-IT', month = 'short', fallback }: DayLabelOptions,
): string => {
  if (!day.date) return fallback(day.index);
  const date = new Date(day.date);
  return `${date.getDate()} ${formatter(locale, { month }).format(date)}`;
};

/** Sottotitolo del giorno: il giorno della settimana, che un template non ha. */
export const formatDayWeekday = (
  day: Pick<DayView, 'date'>,
  locale: string = 'it-IT',
): string => (day.date ? formatter(locale, { weekday: 'long' }).format(new Date(day.date)) : '');

/** Data singola nel formato usato dai riepiloghi (12 mag 2027). */
export const formatDayDate = (date?: string | null, locale: string = 'it-IT'): string =>
  date ? formatter(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(date)) : '';
